// Glowkeep "Make yours" preview: a three.js memory orb that shows the shopper's own photo or video,
// follows the colour chosen in the variant picker, and offers Front/Side/Back views + framing sliders.
// Nothing is uploaded here; files stay in the browser. Falls back to a flat preview without WebGL.
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/controls/OrbitControls.js/+esm';
import { RoomEnvironment } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/environments/RoomEnvironment.js/+esm';

function glassShine(radius){
  const c = document.createElement('canvas'); c.width = 512; c.height = 256; const x = c.getContext('2d');
  x.save(); x.translate(150, 78); x.rotate(-0.35); x.scale(1, 0.42);
  let g = x.createRadialGradient(0, 0, 0, 0, 0, 70); g.addColorStop(0, 'rgba(255,255,255,0.95)'); g.addColorStop(0.45, 'rgba(255,255,255,0.45)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.beginPath(); x.arc(0, 0, 70, 0, 6.283); x.fill(); x.restore();
  g = x.createRadialGradient(214, 58, 0, 214, 58, 12); g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(1, 'rgba(255,255,255,0)');
  x.fillStyle = g; x.beginPath(); x.arc(214, 58, 12, 0, 6.283); x.fill();
  x.save(); x.translate(370, 190); x.rotate(0.6); x.scale(1, 0.12);
  g = x.createRadialGradient(0, 0, 0, 0, 0, 90); g.addColorStop(0, 'rgba(255,240,220,0.55)'); g.addColorStop(1, 'rgba(255,240,220,0)');
  x.fillStyle = g; x.beginPath(); x.arc(0, 0, 90, 0, 6.283); x.fill(); x.restore();
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, 64, 48, 0, Math.PI),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
  mesh.renderOrder = 5;
  return mesh;
}
function glassRim(radius){
  return new THREE.Mesh(new THREE.SphereGeometry(radius, 64, 48, 0, Math.PI), new THREE.ShaderMaterial({
    transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: 'varying vec3 vN; varying vec3 vV; void main(){ vec4 mv = modelViewMatrix * vec4(position,1.0); vN = normalize(normalMatrix * normal); vV = normalize(-mv.xyz); gl_Position = projectionMatrix * mv; }',
    fragmentShader: 'varying vec3 vN; varying vec3 vV; void main(){ float f = pow(1.0 - max(dot(vN, vV), 0.0), 3.0); gl_FragColor = vec4(vec3(0.85,0.92,1.0) * f * 0.55, f); }' }));
}

function buildOrb(root, state){
  const stage = root.querySelector('[data-ref="stage"]');
  const canvas = root.querySelector('[data-ref="canvas"]');
  let renderer;
  renderer = new THREE.WebGLRenderer({ canvas, antialias:true, alpha:true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;

  const scene = new THREE.Scene();
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment(renderer), 0.04).texture;

  const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50);
  camera.position.set(0, 0.15, 5.2);
  const controls = new OrbitControls(camera, canvas);
  controls.enablePan = false; controls.enableZoom = false; controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.target.set(0, 0.05, 0);
  let lastInteract = -1e9;
  canvas.addEventListener('pointerdown', () => { lastInteract = performance.now(); });
  controls.addEventListener('change', () => { if (performance.now() - lastInteract < 4000) lastInteract = performance.now(); });

  scene.add(new THREE.AmbientLight(0xffffff, 0.35));
  const key = new THREE.DirectionalLight(0xffe2b8, 1.6); key.position.set(2.5, 3, 4); scene.add(key);
  const rim = new THREE.PointLight(0x9fd2bd, 6, 12); rim.position.set(-3, 1.5, -3); scene.add(rim);
  const warm = new THREE.PointLight(0xffb060, 4, 10); warm.position.set(2.5, -1.5, 2); scene.add(warm);

  const orb = new THREE.Group(); scene.add(orb);
  const bodyMat = new THREE.MeshPhysicalMaterial({ color: state.color, roughness:0.42, clearcoat:0.4, clearcoatRoughness:0.35 });
  orb.add(new THREE.Mesh(new THREE.SphereGeometry(1, 96, 64, Math.PI, Math.PI), bodyMat));
  orb.add(new THREE.Mesh(new THREE.TorusGeometry(1.0, 0.04, 20, 160), bodyMat));
  const plate = new THREE.Mesh(new THREE.CircleGeometry(0.995, 96), new THREE.MeshStandardMaterial({ color:0x060708, roughness:0.6 })); plate.position.z = 0.001; orb.add(plate);

  const demo = document.createElement('canvas'); demo.width = demo.height = 512;
  const dctx = demo.getContext('2d');
  const demoTex = new THREE.CanvasTexture(demo); demoTex.colorSpace = THREE.SRGBColorSpace;
  const screenMat = new THREE.MeshBasicMaterial({ map: demoTex, toneMapped:false });
  const screen = new THREE.Mesh(new THREE.CircleGeometry(0.86, 128), screenMat); screen.position.z = 0.012; orb.add(screen);

  // Plain glass dome (Davor-tuned, v10): do NOT switch to transmission glass, it mirrors the room and looks milky.
  orb.add(new THREE.Mesh(new THREE.SphereGeometry(1.0, 96, 64, 0, Math.PI), new THREE.MeshPhysicalMaterial({
    color:0xeef3ff, transparent:true, opacity:0.3, roughness:0.02, metalness:0.1, clearcoat:1, clearcoatRoughness:0.02, envMapIntensity:2.4, specularIntensity:1, depthWrite:false })));
  orb.add(glassRim(1.002)); orb.add(glassShine(1.004));

  const dark = new THREE.MeshStandardMaterial({ color:0x111214, roughness:0.5 });
  function onSurface(mesh, x, y){ const z = -Math.sqrt(Math.max(0, 1 - x*x - y*y)); const v = new THREE.Vector3(x, y, z).normalize(); mesh.position.copy(v.clone().multiplyScalar(1.001)); mesh.lookAt(v.clone().multiplyScalar(2)); orb.add(mesh); }
  const usb = new THREE.Mesh(new THREE.CapsuleGeometry(0.035, 0.13, 6, 12), dark); usb.geometry.rotateZ(Math.PI/2); onSurface(usb, 0, -0.02);
  onSurface(new THREE.Mesh(new THREE.BoxGeometry(0.17, 0.02, 0.01), dark), 0.34, 0.0);
  const dotGeo = new THREE.CircleGeometry(0.016, 10);
  for (let r = 0; r < 6; r++) for (let c = -7; c <= 7; c++) {
    const x = c * 0.05 + (r % 2 ? 0.025 : 0), y = 0.32 + r * 0.045;
    if ((x*x)/(0.36*0.36) + ((y-0.43)*(y-0.43))/(0.14*0.14) > 1) continue;
    onSurface(new THREE.Mesh(dotGeo, dark), -x, y);
  }
  const loop = new THREE.Mesh(new THREE.TorusGeometry(0.075, 0.018, 12, 40), new THREE.MeshStandardMaterial({ color:0xc99a4e, metalness:1, roughness:0.28 }));
  loop.position.set(0, 1.07, 0); loop.rotation.y = Math.PI/2; orb.add(loop);

  const sh = document.createElement('canvas'); sh.width = sh.height = 128; const sctx = sh.getContext('2d');
  const g = sctx.createRadialGradient(64,64,4,64,64,64); g.addColorStop(0,'rgba(0,0,0,.55)'); g.addColorStop(1,'rgba(0,0,0,0)'); sctx.fillStyle = g; sctx.fillRect(0,0,128,128);
  const shadow = new THREE.Mesh(new THREE.PlaneGeometry(2.6, 2.6), new THREE.MeshBasicMaterial({ map:new THREE.CanvasTexture(sh), transparent:true, depthWrite:false }));
  shadow.rotation.x = -Math.PI/2; shadow.position.y = -1.18; scene.add(shadow);

  const flakes = Array.from({length:80}, () => ({ x:Math.random()*512, y:Math.random()*512, r:0.8+Math.random()*2.4, s:0.3+Math.random()*0.9, d:Math.random()*6 }));
  const bokeh = Array.from({length:16}, () => ({ x:Math.random()*512, y:Math.random()*512, r:14+Math.random()*34, p:Math.random()*6, h:Math.random() }));
  function drawDemo(t){
    const bg = dctx.createRadialGradient(256, 300, 20, 256, 256, 330); bg.addColorStop(0,'#5a3812'); bg.addColorStop(0.55,'#21281c'); bg.addColorStop(1,'#071210');
    dctx.fillStyle = bg; dctx.fillRect(0,0,512,512);
    for (const b of bokeh){ const a = 0.18 + 0.14*Math.sin(t*0.0012 + b.p); dctx.fillStyle = b.h > .5 ? `rgba(255,196,110,${a})` : `rgba(255,232,190,${a})`; dctx.beginPath(); dctx.arc(b.x, b.y, b.r, 0, 6.283); dctx.fill(); }
    dctx.fillStyle = '#fff3dc'; dctx.textAlign = 'center';
    dctx.font = '58px Gloock, Georgia, serif'; dctx.fillText('Your moment', 256, 248);
    dctx.font = 'italic 58px Gloock, Georgia, serif'; dctx.fillText('here', 256, 308);
    dctx.font = '500 15px "IBM Plex Mono", monospace'; dctx.fillStyle = 'rgba(255,243,220,.7)'; dctx.fillText('ADD A PHOTO OR VIDEO', 256, 352);
    dctx.fillStyle = 'rgba(255,255,255,.85)';
    for (const f of flakes){ f.y += f.s; f.x += Math.sin(t*0.001 + f.d)*0.3; if (f.y > 516){ f.y = -4; f.x = Math.random()*512; } dctx.beginPath(); dctx.arc(f.x, f.y, f.r, 0, 6.283); dctx.fill(); }
    demoTex.needsUpdate = true;
  }

  let mediaTex = null, mediaId = null, usingDemo = true;
  function applyFit(tex, w, h, fit){
    const a = w / h; let rx = 1, ry = 1; if (a > 1) rx = 1 / a; else ry = a;
    rx /= fit.z; ry /= fit.z;
    tex.repeat.set(rx, ry);
    tex.offset.set((1 - rx)/2 + fit.x * (1 - rx)/2, (1 - ry)/2 + fit.y * (1 - ry)/2);
  }
  function apply(s){
    bodyMat.color.set(s.color);
    if (!s.media){ if (mediaTex){ mediaTex.dispose(); mediaTex = null; mediaId = null; } screenMat.map = demoTex; usingDemo = true; screenMat.needsUpdate = true; return; }
    if (s.media.id !== mediaId){
      if (mediaTex) mediaTex.dispose();
      mediaTex = s.media.type === 'video' ? new THREE.VideoTexture(s.media.el) : new THREE.Texture(s.media.el);
      mediaTex.colorSpace = THREE.SRGBColorSpace; mediaTex.needsUpdate = true; mediaId = s.media.id;
      screenMat.map = mediaTex; screenMat.needsUpdate = true; usingDemo = false;
    }
    applyFit(mediaTex, s.media.w, s.media.h, s.fit);
  }
  const views = { front:new THREE.Vector3(0, 0.15, 5.2), side:new THREE.Vector3(3.9, 0.5, 3.4), back:new THREE.Vector3(0, 0.3, -5.2) };
  let camTarget = null;
  function setView(v){ camTarget = views[v] || views.front; lastInteract = performance.now() + 4000; }
  function resize(){ const r = stage.getBoundingClientRect(); if (!r.width) return; renderer.setSize(r.width, r.height, false); camera.aspect = r.width / r.height; camera.fov = camera.aspect < 0.9 ? 34 : 28; camera.updateProjectionMatrix(); }
  new ResizeObserver(resize).observe(stage); resize();

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let visible = true;
  new IntersectionObserver(es => { visible = es[0].isIntersecting; }, { threshold: 0.02 }).observe(stage);
  function frame(t){
    requestAnimationFrame(frame);
    if (!visible) return;
    if (usingDemo) drawDemo(t);
    if (camTarget){ camera.position.lerp(camTarget, 0.08); if (camera.position.distanceTo(camTarget) < 0.01) camTarget = null; }
    if (performance.now() - lastInteract > 4000 && !reduce){ orb.rotation.y += (Math.sin(t * 0.00045) * 0.38 - orb.rotation.y) * 0.03; orb.position.y = Math.sin(t * 0.0009) * 0.03; }
    controls.update(); renderer.render(scene, camera);
  }
  requestAnimationFrame(frame);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => drawDemo(performance.now()));
  return { apply, setView };
}

function initPreview(root){
  const $ = s => root.querySelector(s);
  const cfg = JSON.parse(root.querySelector('[data-gk-config]').textContent);
  const state = { media:null, color: cfg.colours[cfg.initialColour] || Object.values(cfg.colours)[0], fit:{z:1,x:0,y:0}, muted:true, view:'front' };
  const files = []; let current = null;
  let orb = null, flatMode = false;
  const colourName = $('[data-ref="colourName"]');

  function flatApply(){
    const box = $('[data-ref="flatOrb"]'); if (!box) return; box.innerHTML = '';
    box.style.boxShadow = `0 0 0 10px ${state.color}, 0 30px 60px rgba(0,0,0,.5)`;
    if (!state.media){ box.innerHTML = '<div class="flat-demo">Your moment<br>here</div>'; return; }
    const el = state.media.el; el.style.transform = `translate(${state.fit.x*-25}%, ${state.fit.y*25}%) scale(${state.fit.z})`;
    box.appendChild(el); if (el.tagName === 'VIDEO') el.play().catch(()=>{});
  }
  function failed(){ if (flatMode) return; flatMode = true; $('[data-ref="canvas"]').hidden = true; $('[data-ref="flat"]').hidden = false; $('[data-ref="hint"]').textContent = 'Flat preview'; flatApply(); }
  function push(){ if (orb) orb.apply(state); else flatApply(); }
  try { orb = buildOrb(root, state); } catch(e){ console.warn('Glowkeep preview: WebGL unavailable', e); failed(); }
  push();

  // colour follows the variant picker (Horizon writes ?variant= to the URL on change)
  function setColourByName(name){
    if (!name || !cfg.colours[name]) return;
    state.color = cfg.colours[name]; if (colourName) colourName.textContent = name; push();
  }
  let lastVariant = null;
  function syncVariant(){
    const id = new URLSearchParams(location.search).get('variant');
    if (id && id !== lastVariant){ lastVariant = id; setColourByName(cfg.variants[id]); }
  }
  syncVariant(); setInterval(syncVariant, 400);
  document.addEventListener('change', e => {
    const t = e.target; if (!(t instanceof HTMLInputElement) && !(t instanceof HTMLSelectElement)) return;
    const val = t.value; if (cfg.colours[val]) setColourByName(val);
  }, true);

  root.querySelectorAll('.seg button').forEach(b => b.addEventListener('click', () => {
    state.view = b.dataset.view; root.querySelectorAll('.seg button').forEach(x => x.setAttribute('aria-pressed', x===b));
    if (orb) orb.setView(state.view);
  }));

  const input = $('[data-ref="file"]'), drop = $('[data-ref="drop"]'), thumbs = $('[data-ref="thumbs"]'), msg = $('[data-ref="msg"]');
  input.addEventListener('change', () => { addFiles(input.files); input.value=''; });
  ['dragenter','dragover'].forEach(e => drop.addEventListener(e, ev => { ev.preventDefault(); drop.classList.add('over'); }));
  ['dragleave','drop'].forEach(e => drop.addEventListener(e, ev => { ev.preventDefault(); drop.classList.remove('over'); }));
  drop.addEventListener('drop', ev => addFiles(ev.dataTransfer.files));

  function addFiles(list){
    msg.textContent = ''; const problems = [];
    for (const f of list){
      if (files.length >= 12){ problems.push('That’s the 12-file limit for one orb.'); break; }
      const isVid = f.type.startsWith('video/') || /\.(mov|mp4|m4v|hevc|3gp)$/i.test(f.name);
      const isImg = !isVid && (f.type.startsWith('image/') || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name));
      if (!isImg && !isVid){ problems.push(`${f.name} isn’t a photo or video.`); continue; }
      if (f.size > 500*1024*1024){ problems.push(`${f.name} is over 500 MB. Trim it and try again.`); continue; }
      const item = { id: Math.random().toString(36).slice(2), file:f, name:f.name, size:f.size, type: isVid?'video':'image', url:URL.createObjectURL(f), fit:{z:1,x:0,y:0}, ready:false, thumb:null, duration:0, w:1, h:1 };
      files.push(item);
      if (item.type === 'image'){
        const img = new Image();
        img.onload = () => { item.el=img; item.w=img.naturalWidth; item.h=img.naturalHeight; item.thumb=item.url; item.ready=true; renderThumbs(); if (!current) select(item); };
        img.onerror = () => { item.unsupported = true; renderThumbs(); };
        img.src = item.url;
      } else loadVideo(item);
    }
    if (problems.length) msg.textContent = problems.join(' ');
    renderThumbs();
  }
  function loadVideo(item){
    const v = document.createElement('video');
    v.setAttribute('muted',''); v.muted = true; v.defaultMuted = true;
    v.setAttribute('playsinline',''); v.setAttribute('webkit-playsinline',''); v.playsInline = true;
    v.loop = true; v.preload = 'auto';
    let done = false;
    const grabThumb = () => { try {
      const vw = v.videoWidth, vh = v.videoHeight; if (!vw) return;
      const c = document.createElement('canvas'); c.width = c.height = 160; const s = Math.min(vw, vh);
      c.getContext('2d').drawImage(v, (vw-s)/2, (vh-s)/2, s, s, 0, 0, 160, 160);
      item.thumb = c.toDataURL('image/jpeg', .8); renderThumbs(); } catch(e){} };
    const finish = ok => {
      if (done) return; done = true; clearTimeout(timer);
      item.el = v; item.w = v.videoWidth || 1; item.h = v.videoHeight || 1;
      item.duration = isFinite(v.duration) ? v.duration : 0; item.ready = ok; item.unsupported = !ok;
      if (ok){ if (v.requestVideoFrameCallback) v.requestVideoFrameCallback(grabThumb); else setTimeout(grabThumb, 250); if (!current) select(item); else v.pause(); }
      else { v.pause(); msg.textContent = `${item.name} is added, but this browser can’t play its format for the preview. We convert it when we load your orb.`; }
      renderThumbs();
    };
    v.addEventListener('playing', () => { if (v.videoWidth) finish(true); });
    v.addEventListener('loadeddata', () => { if (v.videoWidth && v.readyState >= 2) finish(true); });
    v.addEventListener('error', () => finish(false));
    const timer = setTimeout(() => finish(v.videoWidth > 0), 10000);
    v.src = item.url; try { v.load(); } catch(e){}
    const p = v.play(); if (p && p.catch) p.catch(() => {});
  }
  function fmtDur(s){ s=Math.round(s); return s>=60 ? `${Math.floor(s/60)}m ${s%60}s` : `${s}s`; }
  function renderThumbs(){
    thumbs.innerHTML = '';
    files.forEach(it => {
      const w = document.createElement('div'); w.className='thumb-wrap';
      const b = document.createElement('button'); b.type='button'; b.className='thumb'; b.setAttribute('aria-pressed', current===it); b.setAttribute('aria-label', 'Show ' + it.name + ' on the orb');
      b.innerHTML = it.thumb ? `<img src="${it.thumb}" alt="">` : `<span class="badge" style="bottom:22px">${it.unsupported ? (it.type==='video'?'video ✓':'photo ✓') : 'loading'}</span>`;
      if (it.type==='video' && it.ready) b.innerHTML += `<span class="badge">${fmtDur(it.duration)}</span>`;
      b.addEventListener('click', () => { if (it.ready) select(it); else if (it.unsupported) msg.textContent = `${it.name} can’t be previewed in this browser, but we can still load it.`; });
      const x = document.createElement('button'); x.type='button'; x.className='thumb-x'; x.textContent='✕'; x.setAttribute('aria-label','Remove ' + it.name);
      x.addEventListener('click', () => removeFile(it));
      w.append(b,x); thumbs.appendChild(w);
    });
    const vids = files.filter(f=>f.type==='video'), imgs = files.filter(f=>f.type==='image');
    $('[data-ref="meta"]').innerHTML = files.length ? `<span>${imgs.length} photo${imgs.length===1?'':'s'}</span><span>${vids.length} video${vids.length===1?'':'s'}${vids.length?` · ${fmtDur(vids.reduce((a,f)=>a+f.duration,0))}`:''}</span>` : '';
  }
  function removeFile(it){
    const i = files.indexOf(it); if (i<0) return; files.splice(i,1);
    if (it.el && it.el.tagName==='VIDEO') it.el.pause();
    URL.revokeObjectURL(it.url);
    if (current===it){ current=null; const next = files.find(f=>f.ready); if (next) select(next); else { state.media=null; $('[data-ref="fit"]').hidden=true; $('[data-ref="soundBtn"]').hidden=true; push(); } }
    renderThumbs();
  }
  function select(it){
    if (current && current.el && current.el.tagName==='VIDEO') current.el.pause();
    current = it; state.media = { type:it.type, el:it.el, w:it.w, h:it.h, id:it.id }; state.fit = it.fit;
    if (it.type==='video'){ it.el.currentTime=0; it.el.muted = state.muted; it.el.play().catch(()=>{}); }
    $('[data-ref="soundBtn"]').hidden = it.type!=='video';
    $('[data-ref="fit"]').hidden = false; syncFit(); renderThumbs(); push();
  }
  const fitInputs = { z: $('[data-ref="fitZ"]'), x: $('[data-ref="fitX"]'), y: $('[data-ref="fitY"]') };
  function syncFit(){ fitInputs.z.value=state.fit.z; fitInputs.x.value=state.fit.x; fitInputs.y.value=state.fit.y; }
  Object.entries(fitInputs).forEach(([k, el]) => el.addEventListener('input', e => { state.fit[k] = parseFloat(e.target.value); push(); }));
  $('[data-ref="fitReset"]').addEventListener('click', () => { state.fit.z=1; state.fit.x=0; state.fit.y=0; syncFit(); push(); });
  $('[data-ref="soundBtn"]').addEventListener('click', () => { state.muted = !state.muted; if (current && current.type==='video'){ current.el.muted = state.muted; current.el.play().catch(()=>{}); } $('[data-ref="soundBtn"]').textContent = state.muted ? 'Sound off' : 'Sound on'; });

  const toForm = $('[data-ref="toForm"]');
  if (toForm) toForm.addEventListener('click', e => {
    const form = document.querySelector('form[action*="/cart/add"]');
    if (form){ e.preventDefault(); form.scrollIntoView({ behavior:'smooth', block:'center' }); }
  });
}

document.querySelectorAll('[data-gk-preview]').forEach(root => { try { initPreview(root); } catch(e){ console.error('Glowkeep preview failed', e); } });
